import React, { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { GradientButton } from '@/components/ui/GradientButton';
import BackupProgressPage from '@/components/BackupProgressPage';
import {
  Cloud,
  Share2,
  Shield,
  Download,
  ArrowRight,
  Zap,
  HardDrive,
  Clock
} from 'lucide-react';
import { Button } from '@/components/ui/Button';

const features = [
  {
    icon: <Cloud className="h-6 w-6" />,
    title: "Automatic Cloud Backup",
    description: "Your videos are automatically backed up and secured in the cloud"
  },
  {
    icon: <Share2 className="h-6 w-6" />,
    title: "Cross-Platform Sharing",
    description: "Share your content across multiple platforms with a single click"
  },
  {
    icon: <Shield className="h-6 w-6" />,
    title: "Secure Storage",
    description: "Enterprise-grade encryption keeps your content safe and private"
  },
  {
    icon: <Download className="h-6 w-6" />,
    title: "Bulk Downloads",
    description: "Download multiple videos simultaneously with our efficient system"
  },
  {
    icon: <Zap className="h-6 w-6" />,
    title: "Lightning Fast",
    description: "Optimized for speed with our advanced processing technology"
  },
  {
    icon: <HardDrive className="h-6 w-6" />,
    title: "Unlimited Storage",
    description: "Store all your content without worrying about space limitations"
  }
];

const FeatureExplainer = ({ onContinue }) => {
  const [showBackupPage, setShowBackupPage] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const handleGetStarted = () => {
    if (currentStep < features.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowBackupPage(true);
    }
  };

  const handleBackupComplete = (categoryData) => {
    if (onContinue) {
      onContinue(categoryData);
    }
  };

  if (showBackupPage) {
    return <BackupProgressPage onComplete={handleBackupComplete} />;
  }

  return (
    <div className="h-screen w-screen overflow-hidden relative">
      <style>{`
        @keyframes gradient {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }

        .animated-gradient {
          background: linear-gradient(
            -45deg,
            #1a0f2e,
            #2d1b4e,
            #432a75,
            #31204f,
            #1f1235
          );
          background-size: 400% 400%;
          animation: gradient 15s ease infinite;
        }

        .glow {
          position: absolute;
          width: 40%;
          height: 40%;
          filter: blur(100px);
          border-radius: 50%;
        }

        .glow-1 {
          top: -10%;
          left: -10%;
          background: rgba(123, 31, 162, 0.5);
          animation: move1 10s infinite alternate;
        }

        .glow-2 {
          bottom: -10%;
          right: -10%;
          background: rgba(103, 58, 183, 0.5);
          animation: move2 15s infinite alternate;
        }

        @keyframes move1 {
          0% { transform: translate(0, 0); }
          100% { transform: translate(10%, 10%); }
        }

        @keyframes move2 {
          0% { transform: translate(0, 0); }
          100% { transform: translate(-10%, -10%); }
        }
      `}</style>

      <div className="animated-gradient absolute inset-0">
        <div className="glow glow-1" />
        <div className="glow glow-2" />
      </div>

      <div className="relative h-full w-full flex items-center justify-center">
        <div className="max-w-xl w-full mx-auto p-6">
          {/* Progress Indicator */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center space-x-2">
              {features.map((_, index) => (
                <React.Fragment key={index}>
                  <div className={`w-3 h-3 rounded-full transition-colors duration-300 ${
                    index <= currentStep ? 'bg-white' : 'bg-white/30'
                  }`} />
                  {index < features.length - 1 && (
                    <div className={`w-16 h-1 transition-colors duration-300 ${
                      index < currentStep ? 'bg-white' : 'bg-white/30'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Feature Card */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-xl border border-white/20">
            <div className="text-center space-y-6">
              <div className={`w-20 h-20 mx-auto rounded-full bg-gradient-to-r ${features[currentStep].color} flex items-center justify-center`}>
                <div className="h-10 w-10 text-white" />
              </div>

              <div>
                <h2 className="text-3xl font-bold text-white mb-3">
                  {features[currentStep].title}
                </h2>
                <p className="text-white/80 text-lg">
                  {features[currentStep].description}
                </p>
              </div>

              <Button
                onClick={handleGetStarted}
                className="w-full bg-white text-gray-900 hover:bg-white/90 transition-all duration-200 px-8 py-3 rounded-xl text-lg font-medium shadow-lg group"
              >
                <span className="flex items-center justify-center gap-2">
                  {currentStep === features.length - 1 ? "Get Started" : "Next"}
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureExplainer; 